import { Inject, Injectable, OnDestroy } from "@angular/core";
import { TabTrapService } from "@app/services/TabTrap.service";
import { DOCUMENT } from "@angular/common";
import { HeaderService } from "@app/components/header/header.service";
import { WindowToken } from "@app/util/window.token";
import { CommonService } from "@app/services/CommonService";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { StoreBase } from "@app/services/stores/shared/StoreBase";
import { LogoutService } from "@app/services/Logout.service";
import { OauthService } from "@app/services/oauth.service";
import { UserStore } from "@app/services/stores/UserStore";
import { LoginService } from "@app/services/Login.service";
import { NetworkErrorService } from "./NetworkError.service";
import { RegistrationStore } from "../pages/registration/stores/RegistrationStore";
import {
    ActivatedRoute, ActivatedRouteSnapshot,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router
} from "@angular/router";
import { AppDataService } from "@app/services/appData.service";
import { detect } from "detect-browser";

const loginRedirectStore =
    new StoreBase<{ attempts: { time: number }[] }>('hoapass_login_redirects_', sessionStorage)

@Injectable({ providedIn: "root" })
export class AppRunService implements OnDestroy {

    bodyElement: HTMLBodyElement;
    unauthorizedSubscription: Subscription;
    expiredTokenSubscription: Subscription;
    routerEventSubscription: Subscription;
    previousRouteSnapShot: ActivatedRouteSnapshot;


    constructor(@Inject(TabTrapService) private trapTrapService: TabTrapService,
                @Inject(DOCUMENT) private $document: Document,
                @Inject(WindowToken) private $window: Window,
                @Inject(HeaderService) private headerService: HeaderService,
                @Inject(CommonService) private commonService: CommonService,
                @Inject(LogoutService) private logoutService: LogoutService,
                @Inject(LoginService) private loginService: LoginService,
                @Inject(OauthService) private oauthService: OauthService,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(RegistrationStore) private registrationStore: RegistrationStore,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(AppDataService) private appDataService: AppDataService,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private activatedRoute: ActivatedRoute
    ) {}

    run(): Promise<void> {

        return new Promise<void>((resolve, reject) => {

            this.appDataService.browserInfo = detect();

            const bodyElementCollection = this.$document.getElementsByTagName('body');
            if(bodyElementCollection && bodyElementCollection.length > 0){
                this.bodyElement = bodyElementCollection[0] as HTMLBodyElement;
            }

            if (!sessionStorage.getItem('hoapay_initialLogin')) {
                sessionStorage.setItem('hoapay_initialLogin', "true");
            }

            this.setSubscriptions()
            this.trapTrapService.init();
            this.handleRouterEvents();

            this.addEvents();

            this.loadUserStore(resolve, reject);


        })

    }

    private handleRouterEvents() {

        this.routerEventSubscription = this.router.events.subscribe((event) => {

            if(event instanceof NavigationStart) {
                this.appDataService.changingState = true;
            }

            if (event instanceof NavigationEnd) {

                const currentRouteSnapshot = this.activatedRoute.snapshot.firstChild;
                // HIDE HEADER & FOOTER LOGIC

                // always remove by default and then re-apply if necessary (this ensures no change of state is unaccounted for)
                this.bodyElement.classList.remove('navigation-hide-header');
                this.bodyElement.classList.remove('navigation-hide-footer');

                if (currentRouteSnapshot.data &&
                    (currentRouteSnapshot.data.hideHeader || currentRouteSnapshot.data.hideFooter)) {
                    if (currentRouteSnapshot.data.hideHeader) {
                        this.bodyElement.classList.add('navigation-hide-header');
                    }
                    if (currentRouteSnapshot.data.hideFooter) {
                        this.bodyElement.classList.add('navigation-hide-footer');
                    }
                }

                // GUEST and REGISTERED show navigation logic
                if (currentRouteSnapshot.data && this.previousRouteSnapShot &&
                    (this.previousRouteSnapShot.data == null ||
                        this.previousRouteSnapShot.data.isGuest !== currentRouteSnapshot.data.isGuest)) {
                    if (currentRouteSnapshot.data == null || currentRouteSnapshot.data.isGuest) {
                        this.headerService.setDisplayRightContent(false);
                    } else {
                        this.headerService.setDisplayRightContent(true);
                    }
                }

                //handle the case from guest payment confirmation to guest payment confirmation.
                if (this.previousRouteSnapShot &&
                    this.previousRouteSnapShot.data.section === "guestPaymentSuccess" &&
                    currentRouteSnapshot.data.section === "guestPaymentConfirmation") {
                    // TODO: Check if this is still needed and, if so, how to tie it in with routing functionality
                    // event.preventDefault();
                    this.$window.location.href = this.appDataService.appConfig.get_APP_ENV().HOAPASS_WEB_URL;
                }

                this.previousRouteSnapShot = this.activatedRoute.snapshot.firstChild;

                // TODO: event for after page load
                setTimeout(() => {
                    grunticon.embedIcons(grunticon.getIcons(grunticon.getCSS(grunticon.href)));
                    Loader.init();
                }, 0);
                this.appDataService.changingState = false;
            }

            if (event instanceof NavigationError) {
                this.appDataService.changingState = false;
            }

        });

    }

    private mobileViewUpdate(mq: MediaQueryList) {
        if (mq.matches) {
            // if phone
            this.bodyElement.classList.add('size-is-phone');
            this.bodyElement.classList.remove('size-is-larger-than-phone');
        } else {
            // if larger than phone
            this.bodyElement.classList.add('size-is-larger-than-phone');
            this.bodyElement.classList.remove('size-is-phone');
        }
    }

    private handleExpiredToken() {
        sessionStorage.setItem('hoapay_savedLocation', window.location.href);
        this.oauthService.authenticate().pipe(take(1)).subscribe(resp => {
            // Save the new token
        }, err => {
            console.error(err);
        })
    }

    private handleUnauthorized() {

        const now = Date.now()
        const attempts = loginRedirectStore.get('attempts') || []

        const fiveMinutesAgo = now - (5 * 60 * 1000);
        const attemptsInTheLastFiveMinutes = attempts.filter(attempt => attempt.time > fiveMinutesAgo)

        if (attemptsInTheLastFiveMinutes.length > 2) {
            // if more than two previous attempt was within the last 5 minutes, logout.

            if (confirm('We are experiencing difficulties with your account.\nWould you like to logout?')) {
                loginRedirectStore.remove('attempts')

                // Trying something here for "ISBMDPDPP-6288 - Property Pay Login Page is Cray Cray, FUBAR, and Nuttier than squirrel"
                // It's difficult to know exactly what causes the issue, but logging out will definitely solve it...
                this.logoutService.logout()
                return
            }

        } else {
            loginRedirectStore.set('attempts', [{ time: now }, ...attempts])
            // log the current attempt for a later date
        }

        this.oauthService.authenticate().pipe(take(1)).subscribe(resp => {
            //success
        }, err => {
            //error
        })
    }

    dummyPromise(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            resolve({});
        })
    }

    //TODO See why forcing a promise allows test to work
    // Something about the token check code in the 'then' section.
    // the tests will fail if the token check code doesn't run after a promise is returned.
    // In the future we may want to change either where the
    // token check code goes or how tests are configured so they will still work without the promise.
    private loadUserStore(resolve: (value: (PromiseLike<void> | void)) => void, reject: (reason?: any) => void) {
        this.dummyPromise().then(() => {
            const isRegisteredPath = /\/registered/i.test(this.$window.location.href);
            this.commonService.setInitialState(isRegisteredPath);
            if (isRegisteredPath) {
                if (sessionStorage.getItem("hoapay_hasReceivedToken") === "true" && sessionStorage.getItem("hoapay_accessToken")) {
                    this.oauthService.getUser().pipe(take(1)).subscribe(resp => {
                        this.userStore.setClientId(resp.clientid);
                        this.userStore.setEmail(resp.email);
                        this.userStore.setFirstName(resp.firstname);
                        this.userStore.setLastName(resp.lastname);
                        this.userStore.setName(resp.name);
                        //use the hoahomeowner unless using an internal account, which we will then use the hard coded 7 id until internal is completely removed
                        this.userStore.setHoaHomeownerId(resp.hoahomeowner ? parseInt(resp.hoahomeowner) : 7);
                        this.userStore.setSsoOrigin(resp.ssoorigin ? resp.ssoorigin : '');
                        this.userStore.setUid(resp.uid);
                        this.loginService.login();
                        resolve();
                    }, err => {
                        console.error('Error during Get User')
                        console.error(err)
                        reject();
                        //error
                    })
                } else {
                    this.commonService.setUnauthorized();
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    private addEvents() {

        if (this.$window && this.$window.matchMedia) {
            const max_width = 600;
            const HOA_mediaquery_phone_max_width = max_width + "px";
            const mq = this.$window.matchMedia("(max-width: " + HOA_mediaquery_phone_max_width + ")");

            this.mobileViewUpdate(mq);

            this.$window.addEventListener('resize', () => this.mobileViewUpdate(mq));
            this.$window.addEventListener('load', () => this.mobileViewUpdate(mq));
        }

        const onMessageEvent = (message) => {
            if (message != null && typeof message.data === 'string') {
                if (message.data.indexOf('$LOGOUT$') !== -1) {
                    this.logoutService.logout();
                }
            }
        };

        if (typeof this.$window.addEventListener === 'function' ) {
            this.$window.addEventListener('message', onMessageEvent);
            this.$window.addEventListener("offline", () => {
                this.networkErrorService.disconnected();
            }, false);
            this.$window.addEventListener("online", () => {
                this.networkErrorService.reconnected();
            }, false);
        }

    }
    private setSubscriptions() {

        this.unauthorizedSubscription = this.commonService.unauthorizedSubject$.subscribe(() => {
            this.handleUnauthorized();
        })

        this.expiredTokenSubscription = this.commonService.expiredTokenSubject$.subscribe(() => {
            this.handleExpiredToken();
        })

    }

    ngOnDestroy() {

        if(this.unauthorizedSubscription) {
            this.unauthorizedSubscription.unsubscribe();
        }

        if (this.expiredTokenSubscription) {
            this.expiredTokenSubscription.unsubscribe();
        }

        if(this.routerEventSubscription) {
            this.routerEventSubscription.unsubscribe()
        }

    }

}
